import type { IMediaInstance, PlayOptions, Sound } from '@pixi/sound';
import { sound } from '@pixi/sound';
import EventUtils from './utils/EventUtils';
import { repeatUntilFalse } from '../utils/async-utils';
import { TweenManager } from '../libs/TweenManager/TweenManager';
import type { Platform } from './platform/Platform';

export type SoundType =
  | 'Gameplay_Stack_Pickup'
  | 'Gameplay_Stack_Placement'
  | 'UI_Lose_Screen'
  | 'UI_Win_Screen'
  | 'UI_Button_Click'
  | 'Gameplay_Hex_Jump'
  | 'Gameplay_Hex_Remove'
  | 'Gameplay_Stack_Remove'
  | 'Gameplay_Unlock_Tile'
  | 'Gameplay_Coin_Reward'
  | 'Gameplay_Tape_Peel'
  | 'Gameplay_Rocket_Launch'
  | 'Gameplay_Rocket_Explosion'
  | 'Gameplay_Knot_Pop'
  | 'Gameplay_Hammer_Smash'
  | 'Gameplay_Measure_Tape_Retract'
  | 'Gameplay_Measure_Tape_Remove'
  | 'Diorama_Piece_Appearing'
  | 'Diorama_Piece_Completed'
  | 'Diorama_Hit'
  | 'Gameplay_Breaking_Plank'
  | 'UI_Goal_Banner_Appear'
  | 'UI_Goal_banner_FlyAway'
  | 'Gameplay_Score_Added_AtTopBar'
  | 'Gameplay_Final_Score_Trigger'
  | 'UI_Booster_Unlocked'
  | 'UI_Booster_MoveToPosition_AfterTutorial'
  | 'UI_MoneyPop_AfterDoubling'
  | 'UI_Diorama_Completed_ShareScreen'
  | 'Gameplay_Match_1'
  | 'Gameplay_Match_2'
  | 'Gameplay_Match_3'
  | 'Gameplay_Music'
  | 'Hammer_Movement'
  | 'Hammer_Hit'
  | 'Adding_Event_Token';

export enum SoundTrackId {
  Sfx = 0,
  Music,
}

interface SoundTrack {
  readonly id: SoundTrackId;
  volume: number;
  normalizeVolume: (volume: number) => number;
  readonly activeSounds: Map<SoundType, IMediaInstance | Promise<IMediaInstance>>;
}

export type SoundTrackVolumeGetter = () => number;

class SoundManager extends EventUtils {
  public name = 'Sounds';

  public initiated = false;

  public preloaded = false;

  private readonly tweens: TweenManager = new TweenManager();

  private platform: Platform | null = null;

  // AZ-854: true while an ad (pre-roll / interstitial / rewarded) is playing. The gesture /
  // visibility / focus handlers must NOT bring the game audio back while an ad is active —
  // tapping to pause/resume a pre-roll otherwise unmutes the game over the ad audio.
  private adActive = false;

  private readonly focusCallback: (focused: boolean) => void;

  private readonly tracks: Record<SoundTrackId, SoundTrack> = {
    [SoundTrackId.Sfx]: {
      id: SoundTrackId.Sfx,
      volume: 0,
      normalizeVolume: (volume: number) => (volume / 100) * 0.75,
      activeSounds: new Map<SoundType, IMediaInstance | Promise<IMediaInstance>>(),
    },
    [SoundTrackId.Music]: {
      id: SoundTrackId.Music,
      volume: 0,
      normalizeVolume: (volume: number) => (volume / 100) * 0.3,
      activeSounds: new Map<SoundType, IMediaInstance | Promise<IMediaInstance>>(),
    },
  };

  constructor() {
    super();

    this.addEventListener('pointerdown', () => {
      this.onUserInteraction();
    });
    // AZ-976: iOS Safari treats touchstart as the user gesture that is allowed to resume a
    // suspended/interrupted AudioContext, so listen to it alongside pointerdown.
    this.addEventListener('touchstart', () => {
      this.onUserInteraction();
    });
    this.addEventListener('visibilitychange', () => {
      this.onVisibilityChange();
    });
    // AZ-976: minimizing/restoring the browser and locking/unlocking the device on iOS fire
    // pagehide/pageshow and blur/focus — not always visibilitychange. The Azerion platform's
    // addWindowFocusChangeCallback is a no-op, so bind these on the window ourselves.
    window.addEventListener('pageshow', () => {
      this.onWindowFocusChange(true);
    });
    window.addEventListener('pagehide', () => {
      this.onWindowFocusChange(false);
    });
    this.addEventListener('focus', () => {
      this.onWindowFocusChange(true);
    });
    this.addEventListener('blur', () => {
      this.onWindowFocusChange(false);
    });
    this.focusCallback = this.onWindowFocusChange.bind(this);
    this.mute();
  }

  public init(): void {
    this.preloaded = true;
    this.playSounds();
    Object.values(this.tracks).forEach((track) => {
      this.setVolume(100, track.id);
    });
  }

  public update(deltaTime: number): void {
    this.tweens.update(deltaTime);
  }

  /**
   * AZ-854: mute the game while an ad plays and keep it muted against the gesture /
   * visibility / focus handlers; restore it when the ad ends.
   */
  public setAdActive(active: boolean): void {
    this.adActive = active;
    if (active) {
      this.mute();
    } else {
      // The ad left the AudioContext suspended/interrupted; recover it (with retry).
      this.recoverAudio();
    }
  }

  private onUserInteraction(): void {
    if (document.hidden || this.adActive) {
      return;
    }

    if (!this.initiated) {
      this.initiated = true;
      this.playSounds();
      return;
    }

    // AZ-785: an interstitial suspends the AudioContext, and on skip the browser
    // blocks resume() from the (non-gesture) focus/visibility events — leaving the
    // game silent until the next tap. Keep this gesture handler alive so any real
    // user gesture reliably resumes the context and brings audio back.
    // AZ-976: iOS reports 'interrupted' (not only 'suspended') after lock/unlock or an
    // ad, so treat anything that is not 'running' as needing a resume.
    if (sound.context.audioContext.state !== 'running') {
      this.recoverAudio();
    }
  }

  public playSounds(): void {
    if (!this.initiated || !this.preloaded) return;

    this.unmute();

    Object.values(this.tracks).forEach((track) => {
      this.setVolume(track.volume, track.id);
    });
  }

  public setVolume(volume: number, trackId: SoundTrackId): void {
    const track = this.tracks[trackId];
    track.volume = volume;

    Array.from(track.activeSounds.values()).forEach((activeSound) => {
      this.tweens.removeTweensOf(activeSound);
    });

    this.updateVolume(trackId);
  }

  public play(soundType: SoundType, params?: PlayOptions, trackId: SoundTrackId = SoundTrackId.Sfx): void {
    const track = this.tracks[trackId];
    if (!track) return;

    try {
      const instance = sound.play(this.getSoundName(soundType), {
        ...(params ?? {}),
        complete: (s: Sound) => {
          if (params?.complete) params.complete(s);
          track.activeSounds.delete(soundType);
        },
      });

      track.activeSounds.set(soundType, instance);
      this.updateVolume(trackId);
    } catch (e) {
      console.warn(e);
    }
  }

  public playLoop(trackId: SoundTrackId, soundType: SoundType, fadeDuration = 0): void {
    if (this.isSoundPlaying(trackId, soundType)) {
      this.fadeInSound(trackId, soundType, fadeDuration);
      return;
    }

    if (!this.initiated || !this.preloaded) {
      void (async (): Promise<void> => {
        await repeatUntilFalse(() => !this.initiated || !this.preloaded, 100);
        this.playLoop(trackId, soundType, fadeDuration);
      })();
      return;
    }

    this.play(soundType, { loop: true }, SoundTrackId.Music);

    if (fadeDuration > 0) {
      this.fadeInSound(trackId, soundType, fadeDuration);
    }
  }

  public playDelayed(
    soundType: SoundType,
    delay: number,
    params?: PlayOptions,
    track: SoundTrackId = SoundTrackId.Sfx,
  ): void {
    setTimeout(() => {
      this.play(soundType, params, track);
    }, delay);
  }

  public isSoundPlaying(trackId: SoundTrackId, soundType: SoundType): boolean {
    return this.tracks[trackId].activeSounds.has(soundType);
  }

  public stop(soundType: SoundType, trackId: SoundTrackId = SoundTrackId.Sfx): void {
    const track = this.tracks[trackId];

    const mediaInstance = track.activeSounds.get(soundType);
    if (!mediaInstance) return;

    this.tweens.removeTweensOf(mediaInstance);

    track.activeSounds.delete(soundType);
    sound.stop(this.getSoundName(soundType));
  }

  public mute(): void {
    sound.muteAll();
  }

  public unmute(): void {
    // Resume the AudioContext too: it gets suspended while an ad plays or when the tab
    // is backgrounded, and unmuteAll() alone does not resume it (no sound until the next
    // user gesture otherwise).
    this.tryResumeContext();
    sound.unmuteAll();
  }

  public fadeInSound(trackId: SoundTrackId, soundType: SoundType, duration: number): void {
    const track = this.tracks[trackId];
    if (track.volume === 0) return;

    const fadeInAudio = (media: IMediaInstance): void => {
      media.volume = 0;
      this.tweens.removeTweensOf(media);
      this.tweens.create(media).to({ volume: track.normalizeVolume(track.volume) }, duration, 'quadOut', true);
    };

    const activeSound = this.tracks[trackId].activeSounds.get(soundType);
    if (!activeSound) return;

    if (activeSound instanceof Promise) {
      void activeSound.then(fadeInAudio);
    } else {
      fadeInAudio(activeSound);
    }
  }

  public fadeOutSound(trackId: SoundTrackId, soundType: SoundType, duration: number, stop: boolean): void {
    const track = this.tracks[trackId];
    const activeSound = track.activeSounds.get(soundType);
    if (!activeSound) return;

    if (activeSound && !(activeSound instanceof Promise)) {
      this.tweens.removeTweensOf(activeSound);
      const tween = this.tweens.create(activeSound).to({ volume: 0 }, duration, 'quadOut', true);
      if (stop) {
        tween.onComplete.addOnce(() => {
          this.stop(soundType, trackId);
        });
      }
    }
  }

  private onVisibilityChange(): void {
    if (document.hidden) {
      this.mute();
    } else if (!this.adActive) {
      // iOS Safari fires visibilitychange (not always window focus) on tab switch, and
      // suspends the AudioContext while hidden. AZ-854: skip while an ad is active so
      // returning focus from a paused ad doesn't start the game audio over it.
      this.recoverAudio();
    }
  }

  private getSoundName(soundType: SoundType): string {
    return `sounds/ui/${soundType}.mp3`;
  }

  private updateVolume(trackId: SoundTrackId): void {
    const track = this.tracks[trackId];
    const soundTypes: SoundType[] = Array.from(track.activeSounds.keys());

    soundTypes.forEach((soundType: SoundType) => {
      const soundName = this.getSoundName(soundType as SoundType);
      if (sound.find(soundName)) {
        sound.find(soundName).volume = track.normalizeVolume(track.volume);
      }
    });
  }

  public setPlatform(platform: Platform): void {
    if(this.platform) return;
    this.platform = platform;
    this.platform.addWindowFocusChangeCallback(this.focusCallback);
  }

  private onWindowFocusChange(focused: boolean): void {
    if (focused && !this.adActive) {
      // AZ-854: don't bring the game audio back on focus while an ad is active (pausing/
      // resuming a pre-roll refocuses the game and would overlap the ad audio).
      this.recoverAudio();
    } else if (!focused) {
      this.mute();
    }
  }

  /**
   * AZ-976 (AZ-969 pattern): bring the game audio back after the page returns to the
   * foreground or an ad ends. Resumes the AudioContext, restores mute state and volumes,
   * and retries the resume once after 1s — right after a restore iOS can still report the
   * context as interrupted, so a single immediate resume() is not enough.
   */
  private recoverAudio(): void {
    if (this.adActive || document.hidden) return;

    this.tryResumeContext();
    this.unmute();
    this.playSounds();

    setTimeout(() => {
      this.tryResumeContext();
    }, 1000);
  }

  /**
   * Resume the AudioContext if it is not running. iOS reports 'interrupted' as well as
   * 'suspended' (both are "not running"). Only resumes while the page is visible so we don't
   * fight the intended suspend while hidden. Also binds a PERSISTENT statechange listener
   * once: iOS fires statechange on every interruption (each lock/unlock or ad, not just the
   * first), so a one-shot resume would only recover the first time.
   */
  private tryResumeContext(): void {
    try {
      const ctx = sound.context.audioContext;
      this.bindContextStateChange(ctx);

      if (ctx.state !== 'running' && !document.hidden) {
        ctx.resume().catch(() => {});
      }
    } catch (e) {
      console.warn(e);
    }
  }

  private boundAudioContext: AudioContext | null = null;

  private bindContextStateChange(ctx: AudioContext): void {
    if (this.boundAudioContext === ctx) return;
    this.boundAudioContext = ctx;

    ctx.addEventListener('statechange', () => {
      // Only react to an interruption (context left 'running') while visible and no ad is
      // playing; the 'running' transition needs nothing and must not re-trigger recovery.
      if (ctx.state !== 'running' && !document.hidden && !this.adActive) {
        this.recoverAudio();
      }
    });
  }
}

export const soundManager = new SoundManager();
